import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail 
} from 'firebase/auth';

function OrganiserLogin() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        // Sign up new user
        await createUserWithEmailAndPassword(auth, email, password);
        console.log('User created successfully');
        navigate('/organiser-dashboard');
      } else {
        // Sign in existing user
        await signInWithEmailAndPassword(auth, email, password);
        console.log('User signed in successfully');
        navigate('/organiser-dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // User-friendly error messages
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please sign in instead.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Please sign up.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'DM Sans', sans-serif",
      backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.06) 0%, transparent 60%)'
    },
    card: {
      backgroundColor: '#151c2e',
      borderRadius: '14px',
      padding: isMobile ? '24px' : '48px',
      maxWidth: '450px',
      width: '100%',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      border: '1px solid #2a3555'
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    logo: {
      width: '64px',
      height: '64px',
      borderRadius: '14px',
      marginBottom: '16px'
    },
    title: {
      fontSize: isMobile ? '24px' : '30px',
      fontWeight: '700',
      marginBottom: '8px',
      fontFamily: "'Outfit', sans-serif",
      letterSpacing: '-0.01em'
    },
    subtitle: {
      fontSize: '15px',
      color: '#8b95a8'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '13px',
      fontWeight: '500',
      color: '#8b95a8',
      letterSpacing: '0.02em',
      textTransform: 'uppercase'
    },
    input: {
      padding: '12px 16px',
      backgroundColor: '#1c2640',
      color: '#f0f2f5',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      fontSize: '15px',
      fontFamily: "'DM Sans', sans-serif",
      outline: 'none',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    button: {
      padding: '14px',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 12px rgba(16, 185, 129, 0.25)'
    },
    buttonDisabled: {
      padding: '14px',
      backgroundColor: '#1c2640',
      color: '#5a6478',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: '600',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'not-allowed'
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
      color: '#f87171',
      borderRadius: '10px',
      fontSize: '14px',
      border: '1px solid rgba(239, 68, 68, 0.2)'
    },
    success: {
      padding: '12px 16px',
      backgroundColor: 'rgba(16, 185, 129, 0.12)',
      color: '#34d399',
      borderRadius: '10px',
      fontSize: '14px',
      border: '1px solid rgba(16, 185, 129, 0.2)'
    },
    toggle: {
      textAlign: 'center',
      marginTop: '20px',
      fontSize: '14px',
      color: '#8b95a8'
    },
    link: {
      color: '#34d399',
      cursor: 'pointer',
      fontWeight: '600'
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontFamily: "'DM Sans', sans-serif",
      marginBottom: '24px',
      transition: 'all 0.2s ease'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Back to Home
        </button>

        <div style={styles.header}>
          <img src="/icon.png" alt="Cue Clock Pro" style={styles.logo} />
          <h1 style={styles.title}>{isSignUp ? 'Create Account' : 'Organiser Login'}</h1>
          <p style={styles.subtitle}>
            {isSignUp ? 'Set up your Cue Clock Pro organiser account' : 'Sign in to manage your tournaments'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="organiser@example.com"
              style={styles.input}
              disabled={loading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={styles.input}
              disabled={loading}
            />
          </div>

          {isSignUp && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                style={styles.input}
                disabled={loading}
              />
            </div>
          )}

          {error && <div style={styles.error}>⚠️ {error}</div>}
          {resetEmailSent && (
            <div style={styles.success}>
              ✓ Password reset email sent! Check your inbox.
            </div>
          )}

          <button
            type="submit"
            style={loading ? styles.buttonDisabled : styles.button}
            disabled={loading}
          >
            {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
          </button>

          {!isSignUp && (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <span
                onClick={handlePasswordReset}
                style={{ ...styles.link, fontSize: '14px' }}
              >
                Forgot password?
              </span>
            </div>
          )}
        </form>

        <div style={styles.toggle}>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <span onClick={() => {
            setIsSignUp(!isSignUp);
            setError('');
            setResetEmailSent(false);
          }} style={styles.link}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default OrganiserLogin;